import { Box } from '@mui/material';
import { BoxArrowUpRight } from '@styled-icons/bootstrap/BoxArrowUpRight';
import { PencilSquare } from '@styled-icons/bootstrap/PencilSquare';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { Trash } from '@styled-icons/bootstrap/Trash';
import { Upload } from '@styled-icons/bootstrap/Upload';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { IDocumentTableBodyAction } from 'FeatureModules/Document/Components/Tables/DocumentTableBody';
import { EDocumentsTableColumnKeys } from 'FeatureModules/Document/utils/documentHeaderColumns';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import useDialog from 'hooks/useDialog';
import { EmptyStateCard, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import Filter from 'Components/Filter/Filter';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import ActionContainer from 'Components/ActionContainer';
import useInternalDocuments from '../hooks/useInternalDocument';

const InternalDocuments: FC = () => {
  const {
    error,
    loading,
    documents,
    selectedForDeletion,
    usageSelectedDocuments,
    searchTerm,
    hasNextPage,
    filters,
    handleFilterChange,
    handleFilterReset,
    setSelectedForDeletion,
    handleSearchTermChange,
    handleClickDelete,
    handlePrepareDeletion,
    handlePageEndReached,
  } = useInternalDocuments();
  const { plan, company } = useCompanyPlanContext();
  const navigate = useNavigate();
  const isPartnerUi = plan === ECustomerPlan.FREEMIUM;

  const { openDialog } = useDialog();

  const openEditDocumentDialog = useCallback(
    (document: IBaseDocument) => {
      if (!document.usageCount) {
        openDialog({
          type: 'EDIT_DOCUMENT',
          props: {
            document,
            onDelete: () => handleClickDelete(document),
          },
        });
      }
    },
    [openDialog, handleClickDelete]
  );

  const openUploadDocumentDialog = () => {
    openDialog({
      type: 'UPLOAD_DOCUMENTS',
      props: {
        // In the partnerUI assign yourself by default as a document owner
        defaultOwnedById: isPartnerUi ? company.id : undefined,
      },
    });
  };

  const opeDocumentOverview = (document: IBaseDocument) => {
    navigate(`/documents/internal/${document.id}`);
  };

  const documentTableActions: IDocumentTableBodyAction[] = [
    {
      icon: <BoxArrowUpRight size="15" />,
      tooltip: 'Open in new tab',
      onClick: (document: IBaseDocument) => window.open(document.url, '_blank'),
      dataTestId: 'download-document-btn',
    },
    {
      icon: <PencilSquare size="16" />,
      tooltip: 'Edit Document',
      dataTestId: 'edit-document-btn',
      disableIfDocumentUsed: true,
      disabledTooltip: 'Document is used as proof in an impact claim',
      onClick: (document: IBaseDocument) => openEditDocumentDialog(document),
    },
    {
      icon: <Trash size="18" />,
      tooltip: 'Delete Document',
      dataTestId: 'delete-document-btn',
      disableIfDocumentUsed: true,
      disabledTooltip: 'Document is used as proof in an impact claim',
      onClick: handleClickDelete,
    },
  ];

  if (error) {
    return <ErrorState />;
  }

  if (!documents.length && !searchTerm && !filters.some(({ value }) => !!value) && !loading) {
    return (
      <Box mt={4}>
        <EmptyStateCard
          onCreateItemClick={openUploadDocumentDialog}
          variant={EItemTypes.DOCUMENT}
          background={EBackgroundTypes.DETAILED_DOCUMENTS}
          buttonText="Upload documents"
          size={EAvailableSizes.LARGE}
          title="Upload your documents & certificates to manage your impact & footprint"
          buttonIcon={<Upload size={14} color="white" />}
          data-testid="documents-getting-started-card"
          data-testid-button="create-documents-btn-getting-started"
        />
      </Box>
    );
  }

  return (
    <>
      <Box position="relative">
        <Filter
          filters={filters}
          onFilterChange={handleFilterChange}
          onFilterReset={handleFilterReset}
        />

        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm}
            data-testid="document-search-input"
            placeholder="Search document"
            className="search-input"
            actions={
              <Box display="flex">
                <Box px={1}>
                  <ThemeButton
                    size="medium"
                    color="PRIMARY"
                    startIcon={<Plus size={20} color="white" />}
                    data-testid="create-document-btn"
                    onClick={openUploadDocumentDialog}
                  >
                    Add new documents
                  </ThemeButton>
                </Box>
                {selectedForDeletion.length !== 0 && (
                  <ThemeButton
                    size="medium"
                    color="WHITE"
                    tooltip={'Documents are used as proof in impact claims'}
                    disabled={usageSelectedDocuments > 0}
                    startIcon={<Icon name="delete" size="small" />}
                    onClick={handlePrepareDeletion}
                  >
                    Delete {selectedForDeletion.length}
                  </ThemeButton>
                )}
              </Box>
            }
          />
        </ActionContainer>

        {loading && (
          <Box position="relative" minHeight={200}>
            <Loader />
          </Box>
        )}

        {!!documents.length && !loading && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <DocumentTable
              columns={[
                EDocumentsTableColumnKeys.TITLE,
                EDocumentsTableColumnKeys.USAGE,
                EDocumentsTableColumnKeys.ISSUE_DATE,
                EDocumentsTableColumnKeys.EXPIRY_DATE,
                EDocumentsTableColumnKeys.ACTIONS,
              ]}
              documents={documents}
              selectedDocumentIds={selectedForDeletion}
              onSelectionChange={setSelectedForDeletion}
              onDocumentClick={opeDocumentOverview}
              actions={documentTableActions}
            />
          </InfiniteScrollWrapper>
        )}
      </Box>

      {!documents?.length && !loading && (
        <Box position="relative" minHeight={400}>
          <EmptySearchResults
            message="No search results found"
            action={{
              label: 'Create document',
              onClick: openUploadDocumentDialog,
            }}
            skeletonComponent={<DocumentSkeleton title={searchTerm} />}
          />
        </Box>
      )}
    </>
  );
};

export default InternalDocuments;
